import React, { useEffect, useRef, useState } from 'react';
import Splide from '@splidejs/splide';
import '@splidejs/splide/dist/css/splide-core.min.css';
import { RedesignRelatedLinkCard, RedesignRelatedLinksBlock } from '../../models/tcsm-block-models';
import { WysiwygWithReactLink } from '../wysiwyg-with-react-link';
import { VideoModal } from '../video-modal';
import { TransitionExtension } from '../../helpers/carousel-transition-extension';
import { ContentRecommendationsBlockView } from './content-recommendations-block';

interface Props {
	blockData: RedesignRelatedLinksBlock,
}

function LinkIcon(props: { icon: string }) {
	const { icon } = props;

	if (icon !== 'pdf' && icon !== 'external') {
		return null;
	}

	return (
		<svg className="svg-icon" aria-hidden="true" focusable="false">
			<use xmlnsXlink="http://www.w3.org/1999/xlink" xlinkHref={icon === 'pdf' ? '#pdf-icon-mui' : '#icon-external-link'} />
		</svg>
	);
}

export function RedesignRelatedLinksBlockView(props: Props) {

	const { blockData } = props;

	const items = blockData.relatedItemData || [];

	const splideRef = useRef<HTMLDivElement>(null);

	const [videoEmbedUrl, setVideoEmbedUrl] = useState('');

	useEffect(() => {
		if (!splideRef.current || items.length === 0) {
			return undefined;
		}

		// Same options as wwwroot/Static/javascript/modules/articleCarousel.ts, which drives
		// this block on the non-react site.
		// Ignore the typescript issues on the pagination property and slideFocus, it's just incorrect typings from the library
		const carousel = new Splide(splideRef.current, {
			accessibility: true,
			drag: true,
			perPage: 3,
			perMove: 3,
			// @ts-ignore
			pagination: 'slider',
			// @ts-ignore
			slideFocus: false,
			speed: 500,
			focus: 0,
			keyboard: 'focused',

			gap: '2.5rem',

			classes: {
				pagination: 'splide__pagination article-carousel-pagination carousel-pagination-nums',
				page: 'splide__pagination__page article-carousel-page',
			},
		});

		carousel.mount({ TransitionExtension });

		return () => {
			carousel.destroy();
		};
	}, [blockData.relatedItemData]);

	if (items.length === 0) {
		return null;
	}

	function RenderCategory(card: RedesignRelatedLinkCard) {
		if (!card.categoryName) {
			return null;
		}

		return card.categoryUrl
			? <p><a href={card.categoryUrl}>{card.categoryName}</a></p>
			: <p>{card.categoryName}</p>;
	}

	function RenderCard(card: RedesignRelatedLinkCard) {
		if (card.isVideo) {
			return (
				<>
					{card.videoEmbedUrl && (
						<div className="article-carousel-item-image">
							<a
								href={card.videoEmbedUrl}
								className="article-carousel-item-video-link"
								onClick={(e) => { e.preventDefault(); setVideoEmbedUrl(card.videoEmbedUrl); }}
							>
								<span className="visually-hidden">Click to expand video modal</span>
								{card.thumbnailUrl && <img src={card.thumbnailUrl} alt={card.thumbnailAltText || ''} />}
								<svg className="svg-icon" aria-hidden="true" focusable="false">
									<use xmlnsXlink="http://www.w3.org/1999/xlink" xlinkHref="#play-icon" />
								</svg>
							</a>
						</div>
					)}
					<h5>{card.articleTitle}</h5>
					{card.subtitle && <p>{card.subtitle}</p>}
				</>
			);
		}

		return (
			<>
				<div className="article-carousel-item-image">
					{card.thumbnailUrl && <img src={card.thumbnailUrl} alt={card.thumbnailAltText || ''} />}
				</div>
				<h5>
					<a href={card.linkUrl} target="_blank" rel="noreferrer">
						{card.articleTitle}
						<LinkIcon icon={card.linkIcon} />
					</a>
				</h5>
				{card.subtitle && <p>{card.subtitle}</p>}
			</>
		);
	}

	return (
		<section className="adage-block block">
			<div className="article-carousel-block-inner block-inner">
				<div className="splide" ref={splideRef}>
					<h3 className="article-carousel-header">{blockData.title}</h3>
					<div className="splide__track">
						<ul className="splide__list">
							{/* Slides follow the CMS ContentArea order, mirroring
							    RedesignRelatedLinksBlockView.cshtml; a Content Recommendations
							    placement is one slide whose content loads client-side. */}
							{items.map((item, index) => {
								if (item.recommendation) {
									return (
										<li className="splide__slide related-link-carousel-item" key={`recommendation-${item.recommendation.deliveryApiKey}-${index}`}>
											<ContentRecommendationsBlockView blockData={item.recommendation} />
										</li>
									);
								}

								if (!item.card) {
									return null;
								}

								const { card } = item;

								return (
									<li className="splide__slide related-link-carousel-item" key={`${card.articleTitle}-${index}`}>
										{RenderCategory(card)}
										{RenderCard(card)}
										{card.summaryDescription && (
											<div className="article-carousel-item-description">
												<WysiwygWithReactLink content={card.summaryDescription} />
											</div>
										)}
									</li>
								);
							})}
						</ul>
					</div>
					<div className="splide__arrows carousel-controls article-carousel-controls">
						<button type="button" className="article-carousel-btn article-carousel-btn-prev splide__arrow splide__arrow--prev" aria-label="Previous slide">
							<svg aria-hidden="true" focusable="false">
								<use xmlnsXlink="http://www.w3.org/1999/xlink" xlinkHref="#carousel-arrow-icon" />
							</svg>
						</button>
						<button type="button" className="article-carousel-btn article-carousel-btn-next splide__arrow splide__arrow--next" aria-label="Next slide">
							<svg aria-hidden="true" focusable="false">
								<use xmlnsXlink="http://www.w3.org/1999/xlink" xlinkHref="#carousel-arrow-icon" />
							</svg>
						</button>
					</div>
				</div>
			</div>
			<VideoModal open={!!videoEmbedUrl} onClose={() => setVideoEmbedUrl('')} embedUrl={videoEmbedUrl} />
		</section>
	);
}

export default RedesignRelatedLinksBlockView;
